import { DateTime } from 'luxon'
import { dayBefore, lastDay } from '@/utils/contractEnd'
import { declinedChargeKeys, getBackendErrorCode, getBackendErrorField, getErrorMessage } from '@/composables/useAxiosError'
import { termPeriod } from '@/composables/useProductUtils'
import type { TermSwitchPreview } from '@/types'

type Translate = (key: string, named?: Record<string, unknown>) => string

// How a dialog words the amounts of a switch for the viewer.
export interface SwitchText {
  t: Translate
  // Cents quoted on the account's basis, formatted as the viewer is shown them.
  money: (cents: number) => string
  // A net pay-as-you-go unit rate in cents, formatted as the viewer is shown
  // it, to a fraction of a cent; netMoney rounds such an amount to the cent.
  rate: (netCents: number) => string
  netMoney: (netCents: number) => string
  // A price per unit quoted on the account's basis, to a fraction of a cent.
  unitPrice: (cents: number) => string
  // Whether money and rate show net amounts.
  net: boolean
  // The VAT marker for the amounts money and rate show.
  vat: string
}

// Net pay-as-you-go unit rates in cents, and the net base fee per month.
export interface PaygRates {
  cpuCoreHourCents: number
  memoryGibHourCents: number
  storageGibMonthCents: number
  egressGibCents: number
  baseFeeCents: number
}

export const day = (end: DateTime) => end.toLocaleString(DateTime.DATE_SHORT)

// The German day a Unix-seconds instant falls on.
export const berlinDay = (seconds: number) => day(DateTime.fromSeconds(seconds).setZone('Europe/Berlin'))

// The amounts of a plan paid for a term carry a VAT marker only on the net
// basis, which their texts otherwise leave unsaid.
export const termMoney = (cents: number, text: SwitchText) => (text.net ? `${text.money(cents)} ${text.vat}` : text.money(cents))

// What a switch of a plan paid for a term does now, for the dialog that asks
// to confirm it. Nothing for a switch billed as before. A vServer words its
// charge itself, since its server is resized before the payment arrives.
export function termSwitchSummary(
  preview: TermSwitchPreview,
  text: SwitchText,
  chargeKey = 'common.termSwitch.charge',
  chargeNamed: Record<string, unknown> = {}
): string[] {
  const { t } = text
  const amount = termMoney(preview.amount_cents, text)
  const date = preview.period_end ? day(lastDay(DateTime.fromISO(preview.period_end))) : ''
  const sepa = preview.sepa_hold ? [t('common.termSwitch.refundSepa')] : []
  if (!preview.keeps_term) {
    return preview.direction === 'refund' ? [t('common.termSwitch.leaveRefund', { amount }), ...sepa] : []
  }
  const lines =
    preview.next_period_cents === undefined
      ? []
      : [
          t('common.termSwitch.keepsTerm', {
            date,
            newPrice: termMoney(preview.next_period_cents, text),
            period: termPeriod(preview.billing_freq, t)
          })
        ]
  switch (preview.direction) {
    case 'charge':
      return [...lines, t(chargeKey, { amount, date, ...chargeNamed })]
    case 'refund':
      return [...lines, t('common.termSwitch.refund', { amount, date }), ...sepa]
    default:
      return [...lines, t('common.termSwitch.nothingDue')]
  }
}

// Whether the preview is of a monthly switch, made at charge_cents.
export function isMonthlySwitch(preview: TermSwitchPreview | null): boolean {
  return !!preview && ['upgrade', 'downgrade', 'switch'].includes(preview.direction)
}

// Whether confirming creates a payment obligation, so the button must say so:
// an upgrade, and any switch that charges at the click.
export function paysOnConfirm(preview: TermSwitchPreview | null, upgrade: boolean): boolean {
  if (!preview) return false
  return upgrade || preview.direction === 'charge' || (preview.charge_cents ?? 0) > 0
}

export function usagePrices(rates: PaygRates, text: SwitchText): string {
  const { t } = text
  return t('common.termSwitch.usagePrices', {
    cpuPrice: text.rate(rates.cpuCoreHourCents),
    cpuUnit: t('common.termSwitch.units.cpu'),
    ramPrice: text.rate(rates.memoryGibHourCents),
    ramUnit: t('common.termSwitch.units.ram'),
    storagePrice: text.rate(rates.storageGibMonthCents),
    egressPrice: text.rate(rates.egressGibCents)
  })
}

export interface K8sSwitchNames {
  namespace: string
  oldPlan: string
  newPlan: string
}

// The block above the button of a monthly Kubernetes switch: one paragraph,
// then the SEPA timing of its refund.
export function k8sMonthlyBlock(
  preview: TermSwitchPreview,
  names: K8sSwitchNames,
  text: SwitchText,
  payg: { from: boolean; to: boolean; rates: PaygRates }
): string[] {
  const { t, vat } = text
  const amount = text.money(preview.charge_cents ?? 0)
  const nextStart = preview.next_period_start ? DateTime.fromISO(preview.next_period_start) : null
  const monthEnd = nextStart ? day(dayBefore(nextStart)) : ''
  const nextFirst = nextStart ? day(nextStart.setZone('Europe/Berlin')) : ''
  const refunds = (preview.refund_cents ?? 0) > 0 && !!preview.old_paid_end
  const refund = refunds
    ? t(`common.termSwitch.monthly.${payg.from ? 'k8sRefundBase' : 'k8sRefund'}`, {
        ...names,
        refund: text.money(preview.refund_cents!),
        oldPaidEnd: day(dayBefore(DateTime.fromISO(preview.old_paid_end!)))
      })
    : ''
  const newPrice = text.money(preview.next_period_cents ?? 0)
  const sentences = payg.to
    ? [
        t('common.termSwitch.monthly.k8sChargeBase', { ...names, monthEnd, amount, vat }),
        refund,
        t('common.termSwitch.monthly.k8sNextBase', {
          ...names,
          nextFirst,
          basePrice: newPrice,
          usagePrices: usagePrices(payg.rates, text),
          vat
        })
      ]
    : [
        t('common.termSwitch.monthly.k8sCharge', { ...names, monthEnd, amount, vat }),
        refund,
        payg.from ? t('common.termSwitch.monthly.k8sUsageBefore') : '',
        t('common.termSwitch.monthly.k8sNext', { ...names, nextFirst, newPrice, vat })
      ]
  const block = [...sentences, t('common.termSwitch.monthly.openEnded')].filter(Boolean).join(' ')

  return refunds && preview.sepa_hold ? [block, t('common.termSwitch.refundSepa')] : [block]
}

// The block above the button of a monthly Webhosting upgrade, billed with the
// next invoice at the end of the billing month periodEnd closes.
export function hostingMonthlyBlock(
  preview: TermSwitchPreview,
  names: { identifier: string; oldPlan: string; newPlan: string },
  periodEnd: DateTime,
  text: SwitchText
): string {
  return text.t('common.termSwitch.monthly.hosting', {
    ...names,
    newPrice: text.money(preview.next_period_cents ?? 0),
    periodEnd: day(periodEnd),
    amount: text.money(preview.charge_cents ?? 0),
    vat: text.vat
  })
}

// The block above the button of a switch from the free tier to pay-as-you-go,
// with what the click charges once the preview has it.
export function freeToPaidBlock(
  names: { namespace: string; newPlan: string },
  rates: PaygRates,
  text: SwitchText,
  preview: TermSwitchPreview | null
): string[] {
  const { t, vat } = text
  const basePriceSentence =
    rates.baseFeeCents > 0 ? t('common.termSwitch.freeToPaid.basePriceSentence', { basePrice: text.netMoney(rates.baseFeeCents) }) : ''
  const lines = [
    t('common.termSwitch.freeToPaid.intro', names),
    t('common.termSwitch.freeToPaid.prices', { vat, usagePrices: usagePrices(rates, text), basePriceSentence })
  ]
  if (!preview?.next_period_start) return lines
  const nextStart = DateTime.fromISO(preview.next_period_start)
  return [
    ...lines,
    t('common.termSwitch.freeToPaid.chargeNow', {
      newPlan: names.newPlan,
      monthEnd: day(dayBefore(nextStart)),
      amount: text.money(preview.charge_cents ?? 0),
      nextFirst: day(nextStart.setZone('Europe/Berlin')),
      basePrice: text.money(preview.next_period_cents ?? 0),
      vat
    })
  ]
}

const RESOURCE_UNITS: Record<string, string> = { cpu: 'vCPU', memory_gib: 'GiB', storage_gib: 'GiB', pvcs: '' }

const ERROR_KEYS: Record<string, string> = {
  no_payment_method: 'noPaymentMethod',
  ...declinedChargeKeys({ declined: 'paymentFailed', authenticationRequired: 'authenticationRequired' }),
  quote_required: 'quoteStale',
  quote_stale: 'quoteStale',
  preview_stale: 'quoteStale',
  consent_required: 'consentRequired',
  // Every switch sends the version, so only a build older than its backend
  // can leave it out.
  consent_version_required: 'unavailable',
  terms_required: 'termsRequired',
  terms_acceptance_required: 'termsAcceptanceRequired',
  switch_in_progress: 'inProgress',
  cancellation_pending: 'cancellationPending',
  payment_overdue: 'paymentOverdue',
  unpaid_period: 'paymentOverdue',
  wertersatz_unpaid: 'wertersatzUnpaid',
  withdrawal_settling: 'withdrawalSettling',
  already_withdrawn: 'withdrawn',
  plan_changed: 'planChanged',
  plan_not_active: 'unavailable',
  billing_period_unknown: 'unavailable',
  price_unavailable: 'unavailable',
  not_a_free_tier_namespace: 'unavailable',
  fixed_tier_upgrade_via_cart: 'unavailable',
  payment_unconfirmed: 'unconfirmed',
  // The period change and the vServer resize of a period plan.
  not_on_stripe: 'notOnStripe',
  not_term_keeping: 'unavailable',
  switch_window_open: 'switchWindowOpen',
  switch_payment_due: 'switchPaymentDue',
  resize_in_progress: 'resizeInProgress',
  period_not_started: 'periodNotStarted',
  period_change_scheduled: 'periodChangeScheduled',
  period_unavailable: 'periodUnavailable',
  mail_takeover_pending: 'mailTakeoverPending',
  same_period: 'samePeriod',
  no_change_scheduled: 'noChangeScheduled',
  change_in_effect: 'changeInEffect',
  subscription_busy: 'subscriptionBusy',
  stripe_unavailable: 'stripeUnavailable'
}

// The text for a refused or failed switch, keyed on the backend's code; the
// backend's own message is never shown. A code missing from ERROR_KEYS, such
// as invalid_switch_target, takes its text from getErrorMessage.
export function termSwitchError(err: unknown, t: Translate): string {
  const code = getBackendErrorCode(err)
  if (code === 'quota_does_not_fit') {
    const resource = getBackendErrorField<string>(err, 'resource') ?? ''
    const unit = RESOURCE_UNITS[resource] ?? ''
    const fmt = (n: number | undefined) => `${Math.round((n ?? 0) * 10) / 10}${unit ? ` ${unit}` : ''}`
    return t('common.termSwitch.errors.quotaExceeded', {
      resource: t(`common.termSwitch.resources.${resource in RESOURCE_UNITS ? resource : 'other'}`),
      used: fmt(getBackendErrorField<number>(err, 'used')),
      limit: fmt(getBackendErrorField<number>(err, 'limit'))
    })
  }
  if (code === 'terms_version_stale') return t('cart.checkout.errors.termsVersionStale')
  const key = code ? ERROR_KEYS[code] : undefined

  return key ? t(`common.termSwitch.errors.${key}`) : getErrorMessage(err)
}

// Whether the quote the customer saw must be fetched again before a retry.
export function isStaleQuote(err: unknown): boolean {
  const code = getBackendErrorCode(err)
  return code === 'quote_stale' || code === 'quote_required' || code === 'preview_stale'
}

// Whether the backend asks for the withdrawal consent the dialog did not send.
export function needsConsent(err: unknown): boolean {
  return getBackendErrorCode(err) === 'consent_required'
}

// Whether the switch was refused for terms to accept the dialog did not ask
// for, so the preview has to be read again for the current terms state.
export function needsTerms(err: unknown): boolean {
  return getBackendErrorCode(err) === 'terms_required'
}

// Whether the bundled terms text was replaced by a newer published version.
export function isTermsStale(err: unknown): boolean {
  return getBackendErrorCode(err) === 'terms_version_stale'
}
